// Path: src/components/auth/verification/Verification.tsx
// Updated to enhance redirect logging and ensure stability
// Updated to allow verified uOLSS users to render Verification page
// Updated to feature three distinct containers for SocialLinks, IdSelector, PhoneVerification
// Updated to display three distinct containers for all uOLSS users, fix Spin warning
// Updated to refine disabled logic for SocialLinks, ensure container visibility
// Updated to fix JSX error for <IdSelectorContainer>, refine disabled logic
// Updated to ensure form renders for uOLSS users and dropdown is enabled for unverified users
// Updated to ensure dropdown is enabled for unverified users, add debug logs
// Updated to allow pending uOLSS users to submit social links and phone verification, and access evaluation page
// Updated to fix breadcrumbItems syntax error by using spread operator
// Updated to include Initi in breadcrumb trail
// Fixed TreeNode warning by ensuring key and value match
import React, { useState, useEffect } from 'react';
import { Button, Typography, Breadcrumb, Card, Form, Input, message, Select, Tag, TreeSelect } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { HomeOutlined } from '@ant-design/icons';
import { useUserStore } from '../../../stores/UserStore';
import { useFirebase } from '../../dev/Firebase/Firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { VerificationLevel, VerificationLevelType } from '../../../constants/VerificationLevelEnum';
import { UserRole } from '../../../constants/UserEnums';

const { Title, Text } = Typography;
const { Option } = Select;
const { TreeNode } = TreeSelect;

const VerificationContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  min-height: 100vh;
  padding: 20px;
  font-family: 'Arial', sans-serif;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 768px) {
    padding: 12px;
    .ant-typography {
      font-size: 14px !important;
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #fff;
  margin-bottom: 24px;
  padding: 20px;

  .ant-card-head {
    border-bottom: none;
    padding: 0 20px;
  }

  .ant-card-head-title {
    color: #0427f4;
    font-size: 20px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 12px;
    .ant-card-head-title {
      font-size: 16px;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

interface User {
  uid?: string;
  submittedForVerification?: boolean;
  userRole?: string;
}

interface UserStore {
  user: User | null;
  verified: VerificationLevelType | boolean;
  isLoaded: boolean;
  userRole: string;
}

interface VerificationProps {
  firebase: any; // Replace with FirebaseContextType
}

const Verification: React.FC<VerificationProps> = ({ firebase }) => {
  const userStore = useUserStore() as UserStore;
  const { user, verified, isLoaded, userRole } = userStore;
  const { firestore } = useFirebase();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  console.log('Verification rendering - user:', user, 'verified:', verified, 'userRole:', userRole, 'isLoaded:', isLoaded, 'showForm:', showForm);

  useEffect(() => {
    if (isLoaded && user?.uid) {
      setShowForm(!verified && userRole !== UserRole.ADMIN);
      console.log('Verification: Show form - verified:', verified, 'isAdmin:', userRole === UserRole.ADMIN);
    }
  }, [isLoaded, user, verified, userRole]);

  if (!isLoaded || isLoading) {
    console.log('Verification: Skipping checks - isLoaded:', isLoaded, 'isLoading:', isLoading);
    return <div>Loading...</div>;
  }

  if (userRole === UserRole.ADMIN) {
    return (
      <VerificationContainer>
        <Helmet>
          <title>Verification - One Light System OLS</title>
          <meta
            name="description"
            content="Admin verification page for OLS. Admins do not require verification."
          />
        </Helmet>
        <BreadcrumbWrapper>
          <Breadcrumb
            items={[
              { title: <Link to="/"><HomeOutlined /> Home</Link> },
              { title: <Link to="/admin/users/all">Admin Dashboard</Link> },
              { title: 'Verification' },
            ]}
          />
        </BreadcrumbWrapper>
        <FlexContainer>
          <SectionCard title="Verification">
            <Text style={{ color: '#0427f4', fontSize: '16px' }}>
              Admin accounts do not require verification. Please navigate to the Admin Dashboard.
            </Text>
            <Link to="/admin/users/all">
              <Button type="primary" style={{ marginTop: 16 }}>
                Go to Admin Dashboard
              </Button>
            </Link>
          </SectionCard>
        </FlexContainer>
      </VerificationContainer>
    );
  }

  const handleSubmit = async (values: any) => {
    if (!firestore || !user?.uid) {
      console.error('Verification: Firestore or user UID missing');
      message.error('Cannot submit verification: Missing Firestore or user ID');
      return;
    }
    setIsLoading(true);
    try {
      const verificationRef = doc(firestore, `users/${user.uid}/verifications`, user.uid);
      await setDoc(verificationRef, {
        ...values,
        submittedAt: serverTimestamp(),
        status: 'Pending',
      }, { merge: true });
      await setDoc(doc(firestore, 'users', user.uid), {
        submittedForVerification: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      message.success('Verification submitted successfully!');
      setShowForm(false);
      navigate('/home/ols');
    } catch (error) {
      console.error('Error submitting verification:', error);
      message.error('Failed to submit verification: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Sample image options for social verification (adjust based on actual data)
  const imageOptions = [
    {
      key: 'image-184-1771.1.69.3400-0',
      value: 'image-184-1771.1.69.3400-0', // Match key and value
      title: 'Image 1771.1.69',
      url: 'https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F1771.1.69.png?alt=media&token=bb0c03fb-1c23-403a-869b-78463627bed3',
    },
    // Add more image options as needed
  ];

  const breadcrumbItems = [
    { title: <Link to="/"><HomeOutlined /> Home</Link> },
    { title: <Link to="/home/ols">Main Gate</Link> },
    { title: <Link to="/initi">Initi</Link> },
    { title: 'Verification' },
  ];

  return (
    <VerificationContainer>
      <Helmet>
        <title>Verification - One Light System OLS</title>
        <meta
          name="description"
          content="Verify your account to access OLS features and Sun Light Meditation education. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS Verification, Sun Light Meditation, OLS Dashboard, join OLS"
        />
        <link rel="canonical" href="https://www.olsme.com/verification" />
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb items={breadcrumbItems} />
      </BreadcrumbWrapper>
      <FlexContainer>
        <SectionCard title="Account Verification">
          {verified ? (
            <Text style={{ color: '#0427f4', fontSize: '16px' }}>
              Your account is verified! <Tag color="green">Verified</Tag>
            </Text>
          ) : user?.submittedForVerification ? (
            <Text style={{ color: '#0427f4', fontSize: '16px' }}>
              Verification pending. Please wait for admin approval. <Tag color="orange">Pending</Tag>
            </Text>
          ) : showForm ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              style={{ maxWidth: 600, margin: '0 auto' }}
            >
              <Form.Item
                name="fullName"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
              >
                <Input placeholder="Enter your full name" />
              </Form.Item>
              <Form.Item
                name="verificationType"
                label="Verification Type"
                rules={[{ required: true, message: 'Please select verification type' }]}
              >
                <Select placeholder="Select verification type">
                  <Option value="manual">Manual Verification</Option>
                  <Option value="social">Social Media Verification</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="socialImage"
                label="Social Media Image (if applicable)"
                rules={[{ required: false }]}
              >
                <TreeSelect
                  placeholder="Select social media image"
                  treeData={imageOptions.map(option => ({
                    key: option.key,
                    value: option.value,
                    title: option.title,
                  }))}
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Submit Verification
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Text style={{ color: '#0427f4', fontSize: '16px' }}>
              Verification required. Please submit the form to proceed.
            </Text>
          )}
        </SectionCard>
      </FlexContainer>
    </VerificationContainer>
  );
};

export default Verification;